import { useCombobox, useMultipleSelection } from "downshift";
import {
    CSSProperties,
    KeyboardEvent,
    MutableRefObject,
    ReactNode,
    forwardRef,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import Icon from "../../../base/components/Icon";
import { cx } from "../../utils/cx";
import { Chip } from "../Chip";
import styles from "./Autocomplete.module.scss";

/* ─────────── Floating menu positioning ───────────
   Anchors a portal'd menu to a field element. Updates on scroll/resize
   so it tracks the trigger correctly even when nested in scroll
   containers. Returns the inline style to apply to the menu element. */
const useMenuPosition = (
    fieldRef: MutableRefObject<HTMLDivElement | null>,
    isOpen: boolean,
): CSSProperties => {
    const [style, setStyle] = useState<CSSProperties>({
        position: "fixed",
        top: "-9999px",
        left: "-9999px",
        width: "0px",
    });

    useLayoutEffect(() => {
        if (!isOpen) {
            /* Keep the menu out of flow when closed so there is never
               a one-frame flash at (0,0) in portal mode. */
            setStyle({
                position: "fixed",
                top: "-9999px",
                left: "-9999px",
                width: "0px",
            });
            return;
        }
        const update = () => {
            const el = fieldRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setStyle({
                position: "fixed",
                top: `${rect.bottom + 2}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
            });
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [fieldRef, isOpen]);

    return style;
};

/* SSR guard for `createPortal` (Storybook is CSR but apps may SSR). */
const useIsBrowser = () => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    return mounted;
};

/* ════════════════════════════════════════════════════════════════════════
   Treez DS — Autocomplete
   Source: Figma `Autocomplete` (file qgFv3OQga6RQ4luvSFuGFP, page 17857).

   Composition:
   - `downshift` provides the accessible combobox state machine (search,
     keyboard nav, ARIA, focus management) for both single (`useCombobox`)
     and multi (`useMultipleSelection`) modes.
   - Native `<input>` for typing/filtering (NOT a Radix primitive — Radix
     doesn't ship a combobox yet).
   - `Chip` (Treez DS) for multi-select tokens rendered inside the field.

   Visual states (Figma):
   - Enabled (empty)        → bg grey-03, no border, label centered 15/24.
   - Filled (single value)  → bg grey-03, label up 12/16, value 15/24.
   - Active / Open          → bg grey-01, 2px black border, cursor visible.
   - Disabled               → bg grey-02, label disabled.
   - Error                  → 2px error border + helper in error color.
   - Multi-select           → chips inside the field wrap up to 2 lines.
   - "Press Enter to add"   → free-text creation hint above the menu.
   ════════════════════════════════════════════════════════════════════════ */

export interface AutocompleteOption {
    /** Stable, unique value used internally as React key + identity. */
    value: string;
    /** Visible label rendered in the menu and as the selection token. */
    label: string;
    /** Optional leading icon rendered at the left of the menu item. */
    iconLeft?: ReactNode;
    /** Optional trailing node (e.g. a status badge) rendered at the right of the menu item. */
    trailing?: ReactNode;
    /** Disables this single option. */
    disabled?: boolean;
}

interface BaseAutocompleteProps {
    /** Floating label rendered above the value when filled/focused. */
    label: string;
    /** Available options. Filtered locally as the user types. */
    options: AutocompleteOption[];
    /** Helper text rendered below the field. */
    helperText?: string;
    /**
     * Error state. `true` marks the field invalid; passing a string also
     * sets the helper-text content (and colors it red).
     */
    error?: boolean | string;
    /** Marks as required and appends `*` to the label. */
    required?: boolean;
    /** Disables the field entirely. */
    disabled?: boolean;
    /** Placeholder text shown inside the input when there is no value. */
    placeholder?: string;
    /** Stretches to 100% width of its container. Defaults to `true`. */
    fullWidth?: boolean;
    /**
     * Allows free-text creation: when the user types a value not in the
     * options list, a "Press Enter to add" hint is shown above the menu.
     * On commit (Enter / blur with hint highlighted), `onCreate` fires.
     */
    allowCreate?: boolean;
    /**
     * Called when the user commits a free-text value via the
     * "Press Enter to add" hint. Should return a new `AutocompleteOption`
     * (which the parent typically appends to its options list) — or
     * void to indicate the value was rejected.
     */
    onCreate?: (raw: string) => AutocompleteOption | undefined | void;
    /** Forwarded id (used to associate the floating label & helper text). */
    id?: string;
    /** Forwarded className. */
    className?: string;
    /** Forwarded name (for forms). */
    name?: string;
    /**
     * Multi-select only: max number of chips shown inline before collapsing
     * the remaining selections into a `+N` chip.
     */
    maxVisibleChips?: number;
    /**
     * When `true` (default), the menu is portalled to `document.body`.
     * Set `false` inside dialogs/modals so clicks stay within the dialog
     * DOM and do not dismiss the parent overlay.
     */
    portal?: boolean;
}

/* The component is generic over single vs multi using the `multiple`
   discriminator, so consumers get a strongly-typed `value` / `onChange`. */

export interface SingleAutocompleteProps extends BaseAutocompleteProps {
    multiple?: false;
    /** Controlled value (single). */
    value?: string | null;
    /** Uncontrolled default value (single). */
    defaultValue?: string | null;
    /** Selection change handler (single). */
    onChange?: (value: string | null) => void;
}

export interface MultipleAutocompleteProps extends BaseAutocompleteProps {
    multiple: true;
    /** Controlled value (multi). */
    value?: string[];
    /** Uncontrolled default value (multi). */
    defaultValue?: string[];
    /** Selection change handler (multi). */
    onChange?: (value: string[]) => void;
}

export type AutocompleteProps =
    | SingleAutocompleteProps
    | MultipleAutocompleteProps;

const Chevron = () => (
    <span aria-hidden className={styles.chevron}>
        <Icon icon="chevron-down" />
    </span>
);

/**
 * Treez DS — Autocomplete (Wave 1).
 *
 * Searchable combobox with single- or multi-select modes plus optional
 * free-text creation. Built on `downshift` for full a11y/keyboard
 * support; styled to match the Figma `Autocomplete` design.
 *
 * Accessibility (handled by downshift):
 * - `role="combobox"` with `aria-expanded`, `aria-controls`, `aria-activedescendant`.
 * - Keyboard: ↑/↓ to move highlight, Enter to select, Esc to close,
 *   Backspace (multi) to remove the last chip.
 * - Screen-reader live-region announcements when the menu opens/closes.
 */
export const Autocomplete = forwardRef<HTMLInputElement, AutocompleteProps>(
    function Autocomplete(props, ref) {
        if (props.multiple) {
            return (
                <MultiAutocomplete
                    {...(props as MultipleAutocompleteProps)}
                    ref={ref}
                />
            );
        }
        return (
            <SingleAutocomplete
                {...(props as SingleAutocompleteProps)}
                ref={ref}
            />
        );
    },
);

Autocomplete.displayName = "Autocomplete";

/* ════════════════════════════════════════════════════════════════════════
   SINGLE-SELECT IMPLEMENTATION
   ════════════════════════════════════════════════════════════════════════ */

const SingleAutocomplete = forwardRef<
    HTMLInputElement,
    SingleAutocompleteProps
>(function SingleAutocomplete(
    {
        label,
        options,
        value,
        defaultValue,
        onChange,
        helperText,
        error,
        required,
        disabled,
        placeholder,
        fullWidth = true,
        allowCreate = false,
        onCreate,
        id,
        className,
        name,
        portal = true,
    },
    ref,
) {
    const reactId = useId();
    const fieldId = id ?? `ds-autocomplete-${reactId}`;
    const messageId = `${fieldId}-msg`;

    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
        defaultValue ?? null,
    );
    const selectedValue = isControlled ? value ?? null : uncontrolledValue;

    const selectedOption =
        selectedValue != null
            ? options.find(o => o.value === selectedValue) ?? null
            : null;

    const [inputValue, setInputValue] = useState(selectedOption?.label ?? "");

    const filteredOptions = useMemo(() => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return options;
        return options.filter(o => o.label.toLowerCase().includes(q));
    }, [options, inputValue]);

    const exactMatch = useMemo(
        () =>
            options.some(
                o => o.label.toLowerCase() === inputValue.trim().toLowerCase(),
            ),
        [options, inputValue],
    );
    const showCreateHint =
        allowCreate && inputValue.trim().length > 0 && !exactMatch;

    const {
        isOpen,
        getMenuProps,
        getInputProps,
        getItemProps,
        getLabelProps,
        getToggleButtonProps,
        highlightedIndex,
    } = useCombobox<AutocompleteOption>({
        items: filteredOptions,
        inputValue,
        selectedItem: selectedOption,
        itemToString: item => item?.label ?? "",
        onInputValueChange: ({ inputValue: next }) => {
            setInputValue(next ?? "");
        },
        onSelectedItemChange: ({ selectedItem }) => {
            const nextValue = selectedItem?.value ?? null;
            if (!isControlled) setUncontrolledValue(nextValue);
            onChange?.(nextValue);
            setInputValue(selectedItem?.label ?? "");
        },
    });

    const handleCreateOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
        if (
            e.key === "Enter" &&
            showCreateHint &&
            highlightedIndex < 0 &&
            onCreate
        ) {
            const created = onCreate(inputValue.trim());
            if (created) {
                if (!isControlled) setUncontrolledValue(created.value);
                onChange?.(created.value);
                setInputValue(created.label);
            }
        }
    };

    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : helperText;
    const isFilled = Boolean(selectedOption) || inputValue.length > 0;
    const isEmptySingle = !isFilled;

    const fieldRef = useRef<HTMLDivElement | null>(null);
    const menuStyle = useMenuPosition(fieldRef, isOpen);
    const isBrowser = useIsBrowser();

    const menuNode = (
        <div
            className={cx(styles.menu, isOpen && styles.menuOpen)}
            style={menuStyle}
            {...getMenuProps({
                // Keep focus on the input so the menu is not closed by blur
                // before the click lands on an item.
                onMouseDown: event => event.preventDefault(),
            })}
            data-ui="autocomplete-menu"
        >
            {isOpen && (
                <>
                    {showCreateHint && (
                        <div className={styles.createHint}>
                            <span className={styles.createHintReturn}>↵</span>
                            <span>Press Enter to add </span>
                            <strong>{inputValue.trim()}</strong>
                        </div>
                    )}
                    {filteredOptions.length === 0 && !showCreateHint && (
                        <div className={styles.empty}>No matches</div>
                    )}
                    {filteredOptions.map((opt, index) => (
                        <div
                            className={cx(
                                styles.item,
                                highlightedIndex === index &&
                                    styles.itemHighlighted,
                                selectedOption?.value === opt.value &&
                                    styles.itemSelected,
                                opt.disabled && styles.itemDisabled,
                            )}
                            key={opt.value}
                            {...getItemProps({
                                item: opt,
                                index,
                                disabled: opt.disabled,
                            })}
                        >
                            <span className={styles.itemContent}>
                                {opt.iconLeft && (
                                    <span
                                        aria-hidden
                                        className={styles.itemIcon}
                                    >
                                        {opt.iconLeft}
                                    </span>
                                )}
                                <span className={styles.itemLabel}>
                                    {opt.label}
                                </span>
                            </span>
                            {opt.trailing && (
                                <span className={styles.itemTrailing}>
                                    {opt.trailing}
                                </span>
                            )}
                        </div>
                    ))}
                </>
            )}
        </div>
    );

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                className,
            )}
            data-disabled={disabled || undefined}
            data-empty-single={isEmptySingle || undefined}
            data-error={hasError || undefined}
            data-state={isOpen ? "open" : undefined}
            data-ui="autocomplete"
        >
            <div className={styles.field} ref={fieldRef}>
                <div className={styles.inner}>
                    <label
                        className={styles.label}
                        data-floating={isOpen ? true : undefined}
                        {...getLabelProps()}
                    >
                        {label}
                        {required && " *"}
                    </label>
                    <input
                        aria-describedby={message ? messageId : undefined}
                        aria-invalid={hasError || undefined}
                        className={styles.input}
                        disabled={disabled}
                        id={fieldId}
                        name={name}
                        placeholder={isOpen ? placeholder : label}
                        {...getInputProps({
                            ref,
                            onKeyDown: handleCreateOnEnter,
                        })}
                    />
                </div>
                <button
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    className={styles.toggle}
                    disabled={disabled}
                    type="button"
                    {...getToggleButtonProps()}
                >
                    <Chevron />
                </button>
            </div>

            {portal && isBrowser
                ? createPortal(menuNode, document.body)
                : menuNode}

            {message && (
                <div className={styles.helper} id={messageId}>
                    {message}
                </div>
            )}
        </div>
    );
});
SingleAutocomplete.displayName = "SingleAutocomplete";

/* ════════════════════════════════════════════════════════════════════════
   MULTI-SELECT IMPLEMENTATION
   - Selected values are rendered as Chips inside the field.
   - Backspace on empty input removes the last chip.
   ════════════════════════════════════════════════════════════════════════ */

const MultiAutocomplete = forwardRef<
    HTMLInputElement,
    MultipleAutocompleteProps
>(function MultiAutocomplete(
    {
        label,
        options,
        value,
        defaultValue,
        onChange,
        helperText,
        error,
        required,
        disabled,
        placeholder,
        fullWidth = true,
        allowCreate = false,
        onCreate,
        id,
        className,
        name,
        maxVisibleChips = 3,
        portal = true,
    },
    ref,
) {
    const reactId = useId();
    const fieldId = id ?? `ds-autocomplete-${reactId}`;
    const messageId = `${fieldId}-msg`;

    const isControlled = value !== undefined;
    const [uncontrolledValues, setUncontrolledValues] = useState<string[]>(
        defaultValue ?? [],
    );
    const selectedValues = isControlled ? value ?? [] : uncontrolledValues;

    const selectedItems = useMemo(
        () =>
            selectedValues
                .map(v => options.find(o => o.value === v))
                .filter((o): o is AutocompleteOption => Boolean(o)),
        [options, selectedValues],
    );

    const commit = (next: string[]) => {
        if (!isControlled) setUncontrolledValues(next);
        onChange?.(next);
    };

    const [inputValue, setInputValue] = useState("");

    /* Keep selected options in the menu (Figma shows check marks for
           selected items instead of removing them from the list). */
    const filteredOptions = useMemo(() => {
        const q = inputValue.trim().toLowerCase();
        return options.filter(o => {
            if (!q) return true;
            return o.label.toLowerCase().includes(q);
        });
    }, [options, inputValue]);

    const exactMatch = useMemo(
        () =>
            options.some(
                o => o.label.toLowerCase() === inputValue.trim().toLowerCase(),
            ),
        [options, inputValue],
    );
    const showCreateHint =
        allowCreate && inputValue.trim().length > 0 && !exactMatch;

    const { getDropdownProps, removeSelectedItem } =
        useMultipleSelection<AutocompleteOption>({
            selectedItems,
            onStateChange: ({ selectedItems: next, type }) => {
                switch (type) {
                    case useMultipleSelection.stateChangeTypes
                        .SelectedItemKeyDownBackspace:
                    case useMultipleSelection.stateChangeTypes
                        .SelectedItemKeyDownDelete:
                    case useMultipleSelection.stateChangeTypes
                        .DropdownKeyDownBackspace:
                    case useMultipleSelection.stateChangeTypes
                        .FunctionRemoveSelectedItem: {
                        commit((next ?? []).map(i => i.value));
                        break;
                    }
                    default:
                        break;
                }
            },
        });

    const inputRef = useRef<HTMLInputElement | null>(null);

    const {
        isOpen,
        getMenuProps,
        getInputProps,
        getItemProps,
        getLabelProps,
        getToggleButtonProps,
        highlightedIndex,
    } = useCombobox<AutocompleteOption>({
        items: filteredOptions,
        inputValue,
        selectedItem: null,
        itemToString: item => item?.label ?? "",
        stateReducer: (_state, { changes, type }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick: {
                    return {
                        ...changes,
                        isOpen: true, // keep menu open
                        // Keep highlight on the item just toggled — resetting
                        // to 0 made the first option look "selected".
                        inputValue: "",
                    };
                }
                default:
                    return changes;
            }
        },
        onStateChange: ({ inputValue: nextInput, type, selectedItem }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputChange:
                    setInputValue(nextInput ?? "");
                    break;
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (selectedItem) {
                        const alreadySelected = selectedValues.includes(
                            selectedItem.value,
                        );
                        commit(
                            alreadySelected
                                ? selectedValues.filter(
                                      v => v !== selectedItem.value,
                                  )
                                : [...selectedValues, selectedItem.value],
                        );
                        setInputValue("");
                    }
                    break;
                default:
                    break;
            }
        },
    });

    const handleCreateOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
        if (
            e.key === "Enter" &&
            showCreateHint &&
            highlightedIndex < 0 &&
            onCreate
        ) {
            const created = onCreate(inputValue.trim());
            if (created) {
                commit([...selectedValues, created.value]);
                setInputValue("");
            }
        }
    };

    const hasError = Boolean(error);
    const message = typeof error === "string" ? error : helperText;
    const isFilled = selectedItems.length > 0 || inputValue.length > 0;
    const isEmptyMulti = selectedItems.length === 0 && inputValue.length === 0;
    const visibleChips = selectedItems.slice(0, maxVisibleChips);
    const hiddenCount = Math.max(0, selectedItems.length - visibleChips.length);

    const fieldRef = useRef<HTMLDivElement | null>(null);
    const menuStyle = useMenuPosition(fieldRef, isOpen);
    const isBrowser = useIsBrowser();

    return (
        <div
            className={cx(
                styles.root,
                fullWidth && styles.fullWidth,
                className,
            )}
            data-disabled={disabled || undefined}
            data-empty-multi={isEmptyMulti || undefined}
            data-error={hasError || undefined}
            data-multi
            data-state={isOpen ? "open" : undefined}
            data-ui="autocomplete"
            onClick={() => inputRef.current?.focus()}
        >
            <div className={styles.field} ref={fieldRef}>
                <div className={cx(styles.inner, styles.innerMulti)}>
                    <label
                        className={styles.label}
                        data-floating={isOpen || isFilled ? true : undefined}
                        {...getLabelProps()}
                    >
                        {label}
                        {required && " *"}
                    </label>
                    <div className={styles.chipRow}>
                        {visibleChips.map(item => (
                            <Chip
                                color="grey"
                                disabled={disabled}
                                key={item.value}
                                label={item.label}
                                onRemove={() => removeSelectedItem(item)}
                            />
                        ))}
                        {hiddenCount > 0 && (
                            <Chip
                                color="grey"
                                disabled={disabled}
                                label={`+${hiddenCount}`}
                            />
                        )}
                        <input
                            aria-describedby={message ? messageId : undefined}
                            aria-invalid={hasError || undefined}
                            className={cx(styles.input, styles.inputMulti)}
                            disabled={disabled}
                            id={fieldId}
                            name={name}
                            placeholder={
                                isOpen
                                    ? placeholder
                                    : isEmptyMulti
                                    ? label
                                    : undefined
                            }
                            {...getInputProps(
                                getDropdownProps({
                                    ref: (node: HTMLInputElement | null) => {
                                        inputRef.current = node;
                                        if (typeof ref === "function") {
                                            ref(node);
                                        } else if (ref) {
                                            (
                                                ref as MutableRefObject<HTMLInputElement | null>
                                            ).current = node;
                                        }
                                    },
                                    onKeyDown: handleCreateOnEnter,
                                    preventKeyAction: isOpen,
                                }),
                            )}
                        />
                    </div>
                </div>
                <button
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    className={styles.toggle}
                    disabled={disabled}
                    type="button"
                    {...getToggleButtonProps()}
                >
                    <Chevron />
                </button>
            </div>

            {(() => {
                const menuNode = (
                    <div
                        className={cx(styles.menu, isOpen && styles.menuOpen)}
                        style={menuStyle}
                        {...getMenuProps({
                            onMouseDown: event => event.preventDefault(),
                        })}
                        data-ui="autocomplete-menu"
                    >
                        {isOpen && (
                            <>
                                {showCreateHint && (
                                    <div className={styles.createHint}>
                                        <span
                                            className={styles.createHintReturn}
                                        >
                                            ↵
                                        </span>
                                        <span>Press Enter to add </span>
                                        <strong>{inputValue.trim()}</strong>
                                    </div>
                                )}
                                {filteredOptions.length === 0 &&
                                    !showCreateHint && (
                                        <div className={styles.empty}>
                                            {selectedItems.length ===
                                            options.length
                                                ? "All options selected"
                                                : "No matches"}
                                        </div>
                                    )}
                                {filteredOptions.map((opt, index) => (
                                    <div
                                        className={cx(
                                            styles.item,
                                            highlightedIndex === index &&
                                                styles.itemHighlighted,
                                            selectedValues.includes(
                                                opt.value,
                                            ) && styles.itemSelected,
                                            opt.disabled && styles.itemDisabled,
                                        )}
                                        key={opt.value}
                                        {...getItemProps({
                                            item: opt,
                                            index,
                                            disabled: opt.disabled,
                                        })}
                                    >
                                        <span className={styles.itemContent}>
                                            {opt.iconLeft && (
                                                <span
                                                    aria-hidden
                                                    className={styles.itemIcon}
                                                >
                                                    {opt.iconLeft}
                                                </span>
                                            )}
                                            <span className={styles.itemLabel}>
                                                {opt.label}
                                            </span>
                                        </span>
                                        {opt.trailing && (
                                            <span
                                                className={styles.itemTrailing}
                                            >
                                                {opt.trailing}
                                            </span>
                                        )}
                                        {selectedValues.includes(opt.value) && (
                                            <span
                                                aria-hidden
                                                className={styles.itemCheck}
                                            >
                                                ✓
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                );

                if (portal && isBrowser) {
                    return createPortal(menuNode, document.body);
                }
                return menuNode;
            })()}

            {message && (
                <div className={styles.helper} id={messageId}>
                    {message}
                </div>
            )}
        </div>
    );
});
MultiAutocomplete.displayName = "MultiAutocomplete";
